import type { School } from '@/types/school';

/** Copy aligned with plans-of-study list and staff UX. */
export const PLAN_SCHOOLS_MAPPING_COPY = {
  published: {
    title: 'Published schools',
    description:
      'When you publish, students at the selected schools can start planning courses using this plan. Any in-progress student plans will update automatically with changes. Updates may take up to one day.',
  },
  active: {
    title: 'Active schools',
    description:
      'Status determines whether a plan of study is available to students. Inactive plans of study are not displayed to students in Course Planner.',
  },
} as const;

/**
 * Preserves hidden active schools when replacing the editable school selection.
 *
 * @param originalSchools - Complete persisted active-school references.
 * @param editableSchools - Schools exposed by the current drawer, before search or pagination.
 * @param selectedNids - Newly selected IDs within the editable scope.
 * @returns Replacement IDs containing unchanged hidden schools and the editable selection.
 */
export function mergeActiveSchoolSelection(
  originalSchools: Array<{ nid: string }>,
  editableSchools: School[],
  selectedNids: string[]
): string[] {
  const editableNids = new Set(editableSchools.map((school) => school.nid));
  return Array.from(
    new Set([
      ...originalSchools
        .filter((school) => school.nid && !editableNids.has(school.nid))
        .map((school) => school.nid),
      ...selectedNids.filter((nid) => editableNids.has(nid)),
    ])
  );
}

/**
 * Derives SchoolsDrawer props for published vs active mapping (same rules as plans-of-study grid).
 */
export function getSchoolsListAndSelectionForMappingMode(
  mode: 'published' | 'active',
  publishedSchools: Array<{ nid: string }>,
  activeSchools: Array<{ nid: string }>,
  sortedAllSchools: School[]
): {
  allSchools: School[];
  initialSelectedNids: Set<string>;
  lockedNids?: Set<string>;
} {
  const pub = publishedSchools.filter((s) => s.nid);
  const act = activeSchools.filter((s) => s.nid);
  if (mode === 'active') {
    return {
      allSchools: sortedAllSchools.filter((s) => pub.some((p) => p.nid === s.nid)),
      initialSelectedNids: new Set(act.map((s) => s.nid)),
    };
  }
  return {
    allSchools: sortedAllSchools,
    initialSelectedNids: new Set(pub.map((s) => s.nid)),
    lockedNids: new Set(pub.map((s) => s.nid)),
  };
}

export interface PlanSchoolsMappingDrawerProps {
  planId: string;
  planName: string;
  mode: 'published' | 'active';
  publishedSchools: Array<{ nid: string }>;
  activeSchools: Array<{ nid: string }>;
  allSchoolsSorted: School[];
  /** When true, SchoolsDrawer shows loading instead of an empty list. */
  isSchoolsLoading?: boolean;
  /** View-only: toggles disabled, no save or publish controls (district school staff). */
  readOnly?: boolean;
  /** Called the moment the user clicks Save (before API resolves), so the parent can show a list skeleton. */
  onSaveStart?: () => void;
  /** Called when the save API call settles (success or error). */
  onSaveEnd?: () => void;
  onClose: () => void;
}

function schoolNameForNid(nid: string, schools: School[]): string | undefined {
  return schools.find((s) => s.nid === nid)?.name;
}

function buildSchoolStatusHeading(
  planName: string,
  status: 'active' | 'inactive',
  schoolNids: string[],
  schoolsInDrawer: School[]
): string {
  const n = schoolNids.length;
  if (n === 1) {
    const name = schoolNameForNid(schoolNids[0], schoolsInDrawer);
    return name
      ? `${planName} made ${status} for ${name}`
      : `${planName} made ${status} for 1 school`;
  }
  return `${planName} made ${status} for ${n} schools`;
}

/** Success toast after saving active schools (selected nids = new active set). */
export function buildActiveSchoolsSaveSuccessHeading(
  planName: string,
  selectedSchoolNids: string[],
  schoolsInDrawer: School[]
): string {
  return buildSchoolStatusHeading(planName, 'active', selectedSchoolNids, schoolsInDrawer);
}

/**
 * Success toasts after saving active schools, based on what changed.
 * Emits one heading for newly activated schools and one heading for newly inactivated schools.
 */
export function buildActiveSchoolsChangeSuccessHeadings(
  planName: string,
  previousActiveSchoolNids: string[],
  nextActiveSchoolNids: string[],
  schoolsInDrawer: School[]
): string[] {
  const previousSet = new Set(previousActiveSchoolNids);
  const nextSet = new Set(nextActiveSchoolNids);

  const activated = nextActiveSchoolNids.filter((nid) => !previousSet.has(nid));
  const inactivated = previousActiveSchoolNids.filter((nid) => !nextSet.has(nid));

  const headings: string[] = [];
  if (activated.length > 0) {
    headings.push(buildSchoolStatusHeading(planName, 'active', activated, schoolsInDrawer));
  }
  if (inactivated.length > 0) {
    headings.push(buildSchoolStatusHeading(planName, 'inactive', inactivated, schoolsInDrawer));
  }

  if (headings.length > 0) {
    return headings;
  }

  // Defensive fallback when API succeeds but there are no net changes.
  return [buildActiveSchoolsSaveSuccessHeading(planName, nextActiveSchoolNids, schoolsInDrawer)];
}

/** Success toast after saving published schools (selected nids = new published set). */
export function buildPublishedSchoolsSaveSuccessHeading(
  planName: string,
  selectedSchoolNids: string[],
  schoolsInDrawer: School[]
): string {
  const n = selectedSchoolNids.length;
  if (n === 1) {
    const name = schoolNameForNid(selectedSchoolNids[0], schoolsInDrawer);
    return name ? `${planName} published for ${name}` : `${planName} published for 1 school`;
  }
  return `${planName} published for ${n} schools`;
}

export function buildActiveSchoolsSaveErrorHeading(planName: string): string {
  return `Unable to activate ${planName}`;
}

export function buildPublishedSchoolsSaveErrorHeading(planName: string): string {
  return `Unable to publish ${planName}`;
}
